import type { WebGLRenderer, Scene, PerspectiveCamera } from 'three';
import type { PostPipeline } from '../render/types';
import type { Animated } from '../world/types';

export interface FrameLoopContext {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  pipeline: PostPipeline;
  animated: readonly Animated[];
  skyMesh: { position: { copy(v: { x: number; y: number; z: number }): void } };
  onFrame?: (dt: number) => void;
  animationSeconds?: (nowMs: number) => number;
}

/* A frame interval longer than this is integrated as this, so a stalled tab or a slow frame cannot
   move the camera through walls in one step. */
const MAX_FRAME_DT_S = 0.1;

/* Module scope because bootstrap starts the loop and builds the automation API independently,
   and there is exactly one frame loop per page. */
let renderedFrames = 0;
let simulatedClockS = 0;

export function renderedFrameCount(): number {
  return renderedFrames;
}

/** The app's own clock: the sum of every dt the loop has given onFrame, so it lags real time by
 * whatever the dt cap cut from slow frames. Motion integrated with dt keeps its speeds by it. */
export function simulatedSeconds(): number {
  return simulatedClockS;
}

/* A still draws its one instant twice. The fleet senses the camera as its meshes draw and takes it
   up at its next update (fleetLod.ts), so a single draw showed every ship in the tier of no camera;
   the second draw, the one shown, is the frame a live loop draws once its camera has stood still. */
const STILL_DRAWS = 2;

function drawFrame(ctx: FrameLoopContext, now: number, dt: number): void {
  simulatedClockS += dt;

  ctx.renderer.info.reset();

  const seconds = ctx.animationSeconds?.(now) ?? now / 1000;
  for (const anim of ctx.animated) {
    anim.update(seconds);
  }

  if (ctx.onFrame) {
    ctx.onFrame(dt);
  }

  /* onFrame moves the camera, so the dome is centred only now; centring it earlier drew the sky
     around the previous frame's eye. */
  ctx.skyMesh.position.copy(ctx.camera.position);

  ctx.pipeline.render(ctx.scene, ctx.camera);
  renderedFrames += 1;
}

export function startFrameLoop(ctx: FrameLoopContext, still: boolean): () => void {
  let running = true;
  let lastTime = performance.now();

  function frame(now: number): void {
    if (!running) return;

    const dt = Math.min((now - lastTime) / 1000, MAX_FRAME_DT_S);
    lastTime = now;
    // Still images use the cast's deterministic initial instant, independent of load duration.
    drawFrame(ctx, still ? 0 : now, dt);

    if (still) {
      for (let draw = 1; draw < STILL_DRAWS; draw++) drawFrame(ctx, 0, 0);
      return;
    }
    requestAnimationFrame(frame);
  }

  // Enter must reveal the world even when an embedded preview suspends animation frames. The
  // zero-dt first draw applies the URL/control pose without advancing motion; live ticks still
  // follow vsync, while a still completes both camera-dependent LOD draws immediately.
  frame(lastTime);

  return () => {
    running = false;
  };
}
